import { Types } from "../actionTypes"
import { api, isSessionValid } from '../../api/api'
import { clearLocalDatabase } from '../../db/db'

import {
  setLog,
  types as errorTypes
} from '../../common/utilities/LogError'

import { revokeSessionOnServer } from '../../auth/revokeSession'

const _dispatch = dispatch => {
  return dispatch
}

const deviseAuth = (payload, baseURL) => {
  return function (dispatch) {
    return api.post('/auth/sign_in', payload, { baseURL })
      .then(({ data }) => {
        dispatch({
          type: Types.SET_AUTH,
          payload: data
        })
      })
  }
}

const setLoading = loading => {
  return function (dispatch) {
    dispatch({
      type: Types.SET_BACKDROP_LOADING,
      payload: { loading }
    })
  }
}

const defaultSuccess = r => {
  return function (dispatch) {
    setLoading(false)(dispatch)
    return r
  }
}

const defaultCatch = (error, navigate) => {
  return async function (dispatch) {
    setLoading(false)(dispatch)
    if (error.message === "Network Error") console.log('Network Error')
    else if (error?.response) {
      setLog({
        title: error.response.status,
        type: errorTypes.API_ERROR,
        message: error.response.data.errors,
        api_url: error.response.config.url,
        api_method: error.response.config.method,
        api_params: error.response.config.data
      })
      if (error.response.status === 401) {
        await logoutIfSessionInvalid(navigate)(dispatch)
      }
    } else setLog({ type: errorTypes.API_ERROR, message: error.message })
  }
}

const setSnackbar = payload => {
  return function (dispatch) {
    dispatch({
      type: Types.SET_SNACKBAR_DATA,
      payload
    })
  }
}

const setDomain = domain => {
  return function (dispatch) {
    dispatch({
      type: Types.SET_DOMAIN,
      payload: domain
    })
  }
}

/**
 * Corre el logout destructivo solo si el servidor confirma que la sesión ya no
 * sirve. Único punto donde se toma esa decisión: la vía de UI (`defaultCatch`)
 * y la del sync en background (`lobby:auth-expired` en `useSyncEngine`) pasan
 * las dos por aquí, para que no existan dos criterios divergiendo con el
 * tiempo. Antes el camino del SyncEngine deslogueaba sin preguntar nada, y al
 * correr cada 30 s sin intervención era el que cerraba sesiones solo.
 *
 * @returns {Promise<boolean>} `true` si se cerró la sesión.
 */
const logoutIfSessionInvalid = navigate => {
  return async function (dispatch) {
    if (await isSessionValid()) return false

    await logout(navigate)(dispatch)
    return true
  }
}

const logout = navigate => {
  return async function (dispatch) {
    // Liberar el `client_id` en el servidor ANTES de `CLEAN_AUTH`: ese dispatch
    // ejecuta `clearAuth()` como side-effect en el reducer de `profile`, que
    // borra los tokens con los que este DELETE se autentica.
    //
    // Sin esta llamada cada logout abandonaba su entrada en `users.tokens`, y
    // como el hash solo crece, al pasar `max_number_of_devices` el backend
    // descarta la entrada de menor `expiry` — que con el expiry congelado es la
    // sesión activa más antigua, no la basura acumulada.
    //
    // Best-effort con timeout corto: el kiosko opera offline y el logout local
    // no puede quedar esperando a un servidor que no va a responder.
    await revokeSessionOnServer()

    dispatch({ type: Types.CLEAN_AUTH })

    try {
      await clearLocalDatabase()
    } catch (err) {
      console.warn('[logout] Could not clear local database:', err)
    }

    // `navigate` is optional: callers with no router context (e.g. the
    // background sync engine, which lives outside `<BrowserRouter>`) can
    // call `logout()` with no argument — `RequireAuth` in routes.js already
    // redirects to /login reactively as soon as `profile.email` clears via
    // CLEAN_AUTH above.
    navigate?.('/login')
  }
}

export {
  logout,
  logoutIfSessionInvalid,
  _dispatch,
  defaultSuccess,
  defaultCatch,
  deviseAuth,
  setLoading,
  setSnackbar,
  setDomain
}